import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { TSocialPreviewType } from '../SocialPreviewTypeSelector';
import {
  calculateZoomToFit,
  clampZoom,
  DEFAULT_MAX_ZOOM,
  DEFAULT_MIN_ZOOM,
  DEFAULT_ZOOM,
  DEFAULT_ZOOM_STEP,
} from './PreviewPanel.utils';

export interface IUsePreviewPanelParams {
  contentWidth?: number;
  contentHeight?: number;
  minZoom?: number;
  maxZoom?: number;
  step?: number;
}

export interface IUsePreviewPanelResult {
  zoom: number;
  socialPreviewType: TSocialPreviewType;
  viewportRef: RefObject<HTMLDivElement>;
  contentRef: RefObject<HTMLDivElement>;
  handleZoomChange: (zoom: number) => void;
  handleZoomToFit: () => void;
  handleSocialPreviewTypeChange: (type: TSocialPreviewType) => void;
  resetZoom: () => void;
}

/**
 * Hook that manages zoom state, social preview type selection, and keyboard shortcuts
 * for the preview panel.
 *
 * @param params - Configuration for zoom bounds and content dimensions.
 * @returns State and handlers for the preview panel.
 *
 * @example
 * ```tsx
 * const { zoom, handleZoomChange, viewportRef } = usePreviewPanel({ contentWidth: 300, contentHeight: 250 });
 * ```
 */
export function usePreviewPanel({
  contentWidth,
  contentHeight,
  minZoom = DEFAULT_MIN_ZOOM,
  maxZoom = DEFAULT_MAX_ZOOM,
  step = DEFAULT_ZOOM_STEP,
}: IUsePreviewPanelParams = {}): IUsePreviewPanelResult {
  const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM);
  const [socialPreviewType, setSocialPreviewType] =
    useState<TSocialPreviewType>('facebook_newsfeed');
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.style.setProperty('--p-zoom', String(zoom / 100));
    }
  }, [zoom]);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.style.setProperty(
        '--p-content-width',
        contentWidth ? `${contentWidth}px` : 'auto',
      );
      contentRef.current.style.setProperty(
        '--p-content-height',
        contentHeight ? `${contentHeight}px` : 'auto',
      );
    }
  }, [contentWidth, contentHeight]);

  const handleZoomChange = useCallback(
    (newZoom: number): void => {
      setZoom(clampZoom(newZoom, minZoom, maxZoom));
    },
    [minZoom, maxZoom],
  );

  const handleZoomToFit = useCallback((): void => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) {
      return;
    }

    // Reset zoom to 1 before measuring so we get the content's natural
    // rendered size and the viewport dimensions without scroll overflow.
    content.style.setProperty('--p-zoom', '1');
    const renderedWidth = content.scrollWidth;
    const renderedHeight = content.scrollHeight;
    const viewportWidth = viewport.clientWidth;
    const viewportHeight = viewport.clientHeight;

    const newZoom = calculateZoomToFit(
      renderedWidth,
      renderedHeight,
      viewportWidth,
      viewportHeight,
    );
    const clampedZoom = clampZoom(newZoom, minZoom, maxZoom);
    content.style.setProperty('--p-zoom', String(clampedZoom / 100));
    setZoom(clampedZoom);
  }, [minZoom, maxZoom]);

  const handleSocialPreviewTypeChange = useCallback((type: TSocialPreviewType): void => {
    setSocialPreviewType(type);
  }, []);

  const resetZoom = useCallback((): void => {
    setZoom(DEFAULT_ZOOM);
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent): void {
      const modifier = event.metaKey || event.ctrlKey;
      if (!modifier) {
        return;
      }

      if (event.key === '=' || event.key === '+') {
        event.preventDefault();
        setZoom((prev) => clampZoom(prev + step, minZoom, maxZoom));
      } else if (event.key === '-') {
        event.preventDefault();
        setZoom((prev) => clampZoom(prev - step, minZoom, maxZoom));
      } else if (event.key === '0') {
        event.preventDefault();
        setZoom(DEFAULT_ZOOM);
      } else if (event.key === '1') {
        event.preventDefault();
        handleZoomToFit();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [step, minZoom, maxZoom, handleZoomToFit]);

  return {
    zoom,
    socialPreviewType,
    viewportRef,
    contentRef,
    handleZoomChange,
    handleZoomToFit,
    handleSocialPreviewTypeChange,
    resetZoom,
  };
}
